import type { Tone } from '../../ui/v2';

export const REFUND_REVIEW_PAGE_SIZE = 20;

export type ReviewOutcome = 'refunded' | 'reviewed_no_refund' | 'dismissed';
export type MarkOutcome = Exclude<ReviewOutcome, 'refunded'>;

export interface ReviewObject {
  id: number;
  outcome: ReviewOutcome;
  reviewed_at: string;
  reviewed_by_id: number;
  reviewed_by_email: string;
  notes: string;
  refund_id: number | null;
}

export interface RefundReviewRow {
  order_id: number;
  store_id: number | null;
  store_name: string | null;
  total_price_cents: number | null;
  refunded_total_cents: number | null;
  succeeded_refunds_total_cents: number;
  is_fully_refunded: boolean;
  is_paid: boolean;
  stripe_payment_intent_id: string | null;
  order_status: string;
  channel_order_id: string | null;
  external_order_id: string | null;
  external_status: string | null;
  reviewed: boolean;
  review: ReviewObject | null;
}

export interface RefundRecord {
  id: number;
  amount_cents: number;
  status: string;
  stripe_refund_id: string | null;
  created_at: string;
}

export interface RefundReviewDetail extends RefundReviewRow {
  refunds: RefundRecord[];
}

/** The `reviewed` query parameter, sent as a string. */
export type ReviewedFilter = 'false' | 'true';

export const REVIEWED_FILTER_ITEMS: { key: ReviewedFilter; label: string }[] = [
  { key: 'false', label: 'Needs review' },
  { key: 'true', label: 'Reviewed' },
];

export const emptyNoun = (filter: ReviewedFilter) =>
  filter === 'false' ? 'orders needing review' : 'reviewed orders';

export const MARK_OUTCOME_OPTIONS: { value: MarkOutcome; label: string }[] = [
  { value: 'reviewed_no_refund', label: 'No refund needed' },
  { value: 'dismissed', label: 'Dismiss' },
];

export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export const remainingCents = (row: RefundReviewRow) =>
  (row.total_price_cents || 0) - row.succeeded_refunds_total_cents;

export const canMarkReviewed = (row: RefundReviewRow) => !row.reviewed;

export const canIssueRefund = (row: RefundReviewRow) =>
  !row.reviewed && !row.is_fully_refunded && remainingCents(row) > 0;

/** The amount field starts at the remaining refundable amount, in dollars. */
export const initialRefundAmount = (row: RefundReviewRow) =>
  (remainingCents(row) / 100).toFixed(2);

export type RefundAmountCheck =
  | { ok: true; amountCents: number }
  | { ok: false; error: string };

export function validateRefundAmount(
  input: string,
  row: RefundReviewRow,
): RefundAmountCheck {
  const amountCents = Math.round(parseFloat(input) * 100);
  if (isNaN(amountCents) || amountCents <= 0) {
    return { ok: false, error: 'Please enter a valid amount greater than $0.' };
  }
  const remaining = remainingCents(row);
  if (amountCents > remaining) {
    return {
      ok: false,
      error: `Amount exceeds remaining refundable amount (${formatCents(remaining)}).`,
    };
  }
  return { ok: true, amountCents };
}

/** Order, external and refund statuses share one keyword-based tone. */
export function statusTone(status: string): Tone {
  const s = status.toLowerCase();
  if (s.includes('cancel') || s.includes('reject') || s.includes('fail'))
    return 'danger';
  if (s.includes('succeed') || s.includes('complete') || s.includes('paid'))
    return 'success';
  if (s.includes('pending') || s.includes('progress')) return 'warning';
  return 'neutral';
}

export function outcomeLabel(outcome: string): string {
  switch (outcome) {
    case 'refunded':
      return 'Refunded';
    case 'reviewed_no_refund':
      return 'No refund';
    case 'dismissed':
      return 'Dismissed';
    default:
      return outcome;
  }
}

export function outcomeTone(outcome: string): Tone {
  switch (outcome) {
    case 'refunded':
      return 'success';
    case 'reviewed_no_refund':
      return 'accent';
    default:
      return 'neutral';
  }
}

export const formatReviewedAt = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const formatRefundedAt = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export interface RefundReviewPage {
  rows: RefundReviewRow[];
  totalCount: number;
  totalPages: number;
}

/** Reads a successful list response; anything without `results` is empty. */
export function parseRefundReviewPage(data: any): RefundReviewPage {
  if (data?.results && Array.isArray(data.results)) {
    return {
      rows: data.results,
      totalCount: data.count || 0,
      totalPages:
        data.total_pages ||
        Math.ceil((data.count || 0) / REFUND_REVIEW_PAGE_SIZE),
    };
  }
  return { rows: [], totalCount: 0, totalPages: 1 };
}

/** Message for a non-OK action response: `detail`, then `error`, then the status. */
export async function requestErrorMessage(response: Response) {
  const err = await response.json().catch(() => null);
  return err?.detail || err?.error || `Request failed (${response.status}).`;
}
